/**
 * Diff Tab - Compare original vs modified predictions
 */

import { Box, Typography, Paper, Alert, Accordion, AccordionSummary, AccordionDetails, Button, Grid, Snackbar } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LessonV1_SpineTagsPrediction, TagCandidate, LimiterGroup, Limiter } from '../../types';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { predictionsApi } from '../../services/predictions.service';
import { useAppStore } from '../../store/appStore';

interface DiffTabProps {
  prediction: LessonV1_SpineTagsPrediction;
}

interface TagDiff {
  type: 'added' | 'removed' | 'modified' | 'unchanged';
  original?: TagCandidate;
  modified?: TagCandidate;
  competencyId: string;
  competencyDescription: string;
}

interface CompetencyChange {
  field: string;
  oldValue: any;
  newValue: any;
}

interface LimiterGroupChange {
  type: 'added' | 'removed' | 'modified';
  original?: LimiterGroup;
  modified?: LimiterGroup;
  changes?: CompetencyChange[];
  limiterChanges?: LimiterChange[];
}

interface LimiterChange {
  type: 'added' | 'removed' | 'modified';
  original?: Limiter;
  modified?: Limiter;
}

function computeTagDiffs(original: TagCandidate[], modified: TagCandidate[]): TagDiff[] {
  const diffs: TagDiff[] = [];
  
  // Create maps by competency ID
  const originalMap = new Map<string, TagCandidate>();
  const modifiedMap = new Map<string, TagCandidate>();
  
  original.forEach(tag => {
    if (tag?.competency?.spine_node_id) {
      originalMap.set(tag.competency.spine_node_id, tag);
    }
  });
  
  modified.forEach(tag => {
    if (tag?.competency?.spine_node_id) {
      modifiedMap.set(tag.competency.spine_node_id, tag);
    }
  });
  
  // Find added and modified
  modifiedMap.forEach((modTag, id) => {
    const origTag = originalMap.get(id);
    if (!origTag) {
      diffs.push({
        type: 'added',
        modified: modTag,
        competencyId: id,
        competencyDescription: modTag.competency.description,
      });
    } else {
      // Check if changed
      const isChanged = JSON.stringify(origTag) !== JSON.stringify(modTag);
      if (isChanged) {
        diffs.push({
          type: 'modified',
          original: origTag,
          modified: modTag,
          competencyId: id,
          competencyDescription: modTag.competency.description,
        });
      }
    }
  });
  
  // Find removed
  originalMap.forEach((origTag, id) => {
    if (!modifiedMap.has(id)) {
      diffs.push({
        type: 'removed',
        original: origTag,
        competencyId: id,
        competencyDescription: origTag.competency.description,
      });
    }
  });
  
  return diffs;
}

function getCompetencyChanges(original: TagCandidate, modified: TagCandidate): CompetencyChange[] {
  const changes: CompetencyChange[] = [];
  
  if (original.competency.selected !== modified.competency.selected) {
    changes.push({
      field: 'Selected',
      oldValue: original.competency.selected,
      newValue: modified.competency.selected,
    });
  }
  
  if (original.competency.confidence_score !== modified.competency.confidence_score) {
    changes.push({
      field: 'Confidence',
      oldValue: original.competency.confidence_score,
      newValue: modified.competency.confidence_score,
    });
  }
  
  if (original.competency.reasoning !== modified.competency.reasoning) {
    changes.push({
      field: 'Reasoning',
      oldValue: original.competency.reasoning,
      newValue: modified.competency.reasoning,
    });
  }
  
  return changes;
}

function getLimiterGroupChanges(original: TagCandidate, modified: TagCandidate): LimiterGroupChange[] {
  const changes: LimiterGroupChange[] = [];
  
  const origLgMap = new Map<string, LimiterGroup>();
  const modLgMap = new Map<string, LimiterGroup>();
  
  (original.limiter_groups || []).forEach(lg => {
    origLgMap.set(lg.limiter_group_id, lg);
  });
  
  (modified.limiter_groups || []).forEach(lg => {
    modLgMap.set(lg.limiter_group_id, lg);
  });
  
  // Find all limiter group IDs
  const allLgIds = new Set([...origLgMap.keys(), ...modLgMap.keys()]);
  
  allLgIds.forEach(lgId => {
    const origLg = origLgMap.get(lgId);
    const modLg = modLgMap.get(lgId);
    
    if (!origLg && modLg) {
      changes.push({ type: 'added', modified: modLg });
    } else if (origLg && !modLg) {
      changes.push({ type: 'removed', original: origLg });
    } else if (origLg && modLg) {
      const lgChanges: CompetencyChange[] = [];
      const limiterChanges = getLimiterChanges(origLg, modLg);
      
      if (origLg.selected !== modLg.selected) {
        lgChanges.push({ field: 'Selected', oldValue: origLg.selected, newValue: modLg.selected });
      }
      if (origLg.confidence_score !== modLg.confidence_score) {
        lgChanges.push({ field: 'Confidence', oldValue: origLg.confidence_score, newValue: modLg.confidence_score });
      }
      if (origLg.reasoning !== modLg.reasoning) {
        lgChanges.push({ field: 'Reasoning', oldValue: origLg.reasoning, newValue: modLg.reasoning });
      }
      
      if (lgChanges.length > 0 || limiterChanges.length > 0) {
        changes.push({
          type: 'modified',
          original: origLg,
          modified: modLg,
          changes: lgChanges,
          limiterChanges,
        });
      }
    }
  });
  
  return changes;
}

function getLimiterChanges(origLg: LimiterGroup, modLg: LimiterGroup): LimiterChange[] {
  const changes: LimiterChange[] = [];
  
  const origLimMap = new Map<string, Limiter>();
  const modLimMap = new Map<string, Limiter>();
  
  (origLg.limiters || []).forEach(lim => {
    origLimMap.set(lim.limiter_id, lim);
  });
  
  (modLg.limiters || []).forEach(lim => {
    modLimMap.set(lim.limiter_id, lim);
  });
  
  const allLimIds = new Set([...origLimMap.keys(), ...modLimMap.keys()]);
  
  allLimIds.forEach(limId => {
    const origLim = origLimMap.get(limId);
    const modLim = modLimMap.get(limId);
    
    if (!origLim && modLim) {
      changes.push({ type: 'added', modified: modLim });
    } else if (origLim && !modLim) {
      changes.push({ type: 'removed', original: origLim });
    } else if (origLim && modLim) {
      if (origLim.selected !== modLim.selected ||
          origLim.confidence_score !== modLim.confidence_score ||
          origLim.reasoning !== modLim.reasoning) {
        changes.push({ type: 'modified', original: origLim, modified: modLim });
      }
    }
  });
  
  return changes;
}

function CompetencyChangesCard({ diff }: { diff: TagDiff }) {
  if (diff.type === 'added' || diff.type === 'removed') {
    return null; // These are handled separately
  }

  if (!diff.original || !diff.modified) {
    return null;
  }

  const competencyChanges = getCompetencyChanges(diff.original, diff.modified);
  const limiterGroupChanges = getLimiterGroupChanges(diff.original, diff.modified);

  if (competencyChanges.length === 0 && limiterGroupChanges.length === 0) {
    return null;
  }

  return (
    <Accordion defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            📝 {diff.competencyDescription.substring(0, 80)}
            {diff.competencyDescription.length > 80 ? '...' : ''}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          {/* Competency-level changes */}
          {competencyChanges.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                🎯 Competency Changes
              </Typography>
              <Box sx={{ ml: 2 }}>
                {competencyChanges.map((change, idx) => (
                  <Box key={idx} sx={{ mb: 1 }}>
                    {change.field === 'Selected' ? (
                      <Typography variant="body2">
                        <strong>{change.field}:</strong>{' '}
                        {change.oldValue ? '✅' : '❌'} {String(change.oldValue)} →{' '}
                        {change.newValue ? '✅' : '❌'} {String(change.newValue)}
                      </Typography>
                    ) : change.field === 'Confidence' ? (
                      <Typography variant="body2">
                        <strong>{change.field}:</strong> {change.oldValue?.toFixed(2)} → {change.newValue?.toFixed(2)}
                      </Typography>
                    ) : (
                      <Accordion sx={{ mt: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="body2"><strong>{change.field}</strong> changed</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="textSecondary">Original</Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {change.oldValue || 'N/A'}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="textSecondary">Modified</Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {change.newValue || 'N/A'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Limiter group changes */}
          {limiterGroupChanges.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                📋 Limiter Group Changes
              </Typography>
              <Box sx={{ ml: 2 }}>
                {limiterGroupChanges.map((lgChange, idx) => (
                  <LimiterGroupChangeCard key={idx} change={lgChange} />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function LimiterGroupChangeCard({ change }: { change: LimiterGroupChange }) {
  const lg = change.modified || change.original;
  if (!lg) return null;

  const description = lg.description.substring(0, 60) + (lg.description.length > 60 ? '...' : '');

  if (change.type === 'added') {
    return (
      <Accordion sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2">➕ <strong>{description}</strong> (Added)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Selected:</strong> {lg.selected ? '✅' : '❌'} {String(lg.selected)}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Confidence:</strong> {lg.confidence_score?.toFixed(2)}
          </Typography>
          {lg.limiters && lg.limiters.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Limiters:</Typography>
              {lg.limiters.map((lim, idx) => (
                <Typography key={idx} variant="body2" sx={{ ml: 2, fontSize: '0.875rem' }}>
                  • {lim.selected ? '✅' : '❌'} {lim.description} ({lim.type})
                </Typography>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  }

  if (change.type === 'removed') {
    return (
      <Accordion sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2">➖ <strong>{description}</strong> (Removed)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Selected:</strong> {lg.selected ? '✅' : '❌'} {String(lg.selected)}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Confidence:</strong> {lg.confidence_score?.toFixed(2)}
          </Typography>
        </AccordionDetails>
      </Accordion>
    );
  }

  // Modified
  return (
    <Accordion sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2">✏️ <strong>{description}</strong> (Modified)</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {change.changes && change.changes.length > 0 && (
          <Box sx={{ mb: 1 }}>
            {change.changes.map((fieldChange, idx) => (
              <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                {fieldChange.field === 'Selected' ? (
                  <>
                    <strong>{fieldChange.field}:</strong>{' '}
                    {fieldChange.oldValue ? '✅' : '❌'} → {fieldChange.newValue ? '✅' : '❌'}
                  </>
                ) : (
                  <>
                    <strong>{fieldChange.field}:</strong> {fieldChange.oldValue?.toFixed?.(2) || fieldChange.oldValue} →{' '}
                    {fieldChange.newValue?.toFixed?.(2) || fieldChange.newValue}
                  </>
                )}
              </Typography>
            ))}
          </Box>
        )}
        {change.limiterChanges && change.limiterChanges.length > 0 && (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>Limiter Changes:</Typography>
            {change.limiterChanges.map((limChange, idx) => (
              <LimiterChangeDisplay key={idx} change={limChange} />
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

function LimiterChangeDisplay({ change }: { change: LimiterChange }) {
  const lim = change.modified || change.original;
  if (!lim) return null;

  if (change.type === 'added') {
    return (
      <Typography variant="body2" sx={{ ml: 2, fontSize: '0.875rem' }}>
        ➕ <strong>{lim.description}</strong>
      </Typography>
    );
  }

  if (change.type === 'removed') {
    return (
      <Typography variant="body2" sx={{ ml: 2, fontSize: '0.875rem' }}>
        ➖ <strong>{lim.description}</strong>
      </Typography>
    );
  }

  // Modified
  const oldSel = change.original?.selected;
  const newSel = change.modified?.selected;
  return (
    <Typography variant="body2" sx={{ ml: 2, fontSize: '0.875rem' }}>
      ✏️ <strong>{lim.description}:</strong> {oldSel ? '✅' : '❌'} → {newSel ? '✅' : '❌'}
    </Typography>
  );
}

export default function DiffTab({ prediction }: DiffTabProps) {
  const queryClient = useQueryClient();
  const { selectedProduct, selectedExperiment } = useAppStore();
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const original = prediction.tag_prediction_original || [];
  const modified = prediction.tag_prediction_modified || [];

  // Filter out invalid entries
  const validOriginal = Array.isArray(original) ? original.filter(t => t && t.competency) : [];
  const validModified = Array.isArray(modified) ? modified.filter(t => t && t.competency) : [];

  const diffs = computeTagDiffs(validOriginal, validModified);
  const hasChanges = diffs.some(d => d.type !== 'unchanged');

  const addedDiffs = diffs.filter(d => d.type === 'added');
  const removedDiffs = diffs.filter(d => d.type === 'removed');
  const modifiedDiffs = diffs.filter(d => d.type === 'modified');

  // Mutation for restoring original predictions
  const restoreMutation = useMutation({
    mutationFn: async () => {
      console.log('[DiffTab] Restoring original predictions for:', prediction.id);
      
      // Set modified back to original
      const updates: Partial<LessonV1_SpineTagsPrediction> = {
        tag_prediction_modified: prediction.tag_prediction_original,
      };

      return await predictionsApi.update(
        selectedProduct,
        selectedExperiment,
        prediction.id,
        updates
      );
    },
    onSuccess: () => {
      console.log('[DiffTab] Successfully restored original predictions');
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ 
        queryKey: ['prediction', selectedProduct, selectedExperiment, prediction.id] 
      });
      queryClient.invalidateQueries({ queryKey: ['predictions'] });
      
      setSuccessMessage('Successfully restored original AI predictions!');
      setShowRestoreDialog(false);
    },
    onError: (error: any) => {
      console.error('[DiffTab] Error restoring predictions:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to restore original predictions';
      setErrorMessage(errorMsg);
      setShowRestoreDialog(false);
    },
  });

  const handleRestoreOriginal = () => {
    restoreMutation.mutate();
  };

  return (
    <Box>
      {/* Summary Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6">Summary</Typography>
          <Button 
            variant="contained" 
            color="error"
            size="small"
            onClick={() => setShowRestoreDialog(true)}
            disabled={!hasChanges || restoreMutation.isPending}
          >
            {restoreMutation.isPending ? '🔄 Restoring...' : '🔄 Restore Original'}
          </Button>
        </Box>
        
        {!hasChanges ? (
          <Alert severity="success" sx={{ color: 'success.dark' }}>
            ✨ No changes made to predictions
          </Alert>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">{diffs.length}</Typography>
                <Typography variant="body2" color="textSecondary">Total Changes</Typography>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">{addedDiffs.length}</Typography>
                <Typography variant="body2" color="textSecondary">➕ Added</Typography>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="h4" color="error.main">{removedDiffs.length}</Typography>
                <Typography variant="body2" color="textSecondary">➖ Removed</Typography>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main">{modifiedDiffs.length}</Typography>
                <Typography variant="body2" color="textSecondary">✏️ Modified</Typography>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* No changes message */}
      {!hasChanges && (
        <Alert severity="info" sx={{ color: 'info.dark' }}>
          The current predictions match the original AI predictions exactly.
        </Alert>
      )}

      {/* Modified Competencies Section */}
      {modifiedDiffs.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            ✏️ Modified Competencies
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
            {modifiedDiffs.length} competency(ies) with changes
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {modifiedDiffs.map((diff, index) => (
              <CompetencyChangesCard key={`${diff.competencyId}-${index}`} diff={diff} />
            ))}
          </Box>
        </Box>
      )}

      {/* Removed Competencies Section */}
      {removedDiffs.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            ➖ Removed Competencies
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
            {removedDiffs.length} competency(ies) removed from original predictions
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {removedDiffs.map((diff, index) => (
              <Accordion key={`${diff.competencyId}-${index}`}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">
                    ➖ {diff.competencyDescription.substring(0, 80)}
                    {diff.competencyDescription.length > 80 ? '...' : ''}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {diff.original && (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Selected:</strong> {diff.original.competency.selected ? '✅' : '❌'}{' '}
                        {String(diff.original.competency.selected)}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Confidence:</strong> {diff.original.competency.confidence_score?.toFixed(2) || 'N/A'}
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      )}

      {/* Added Competencies Section */}
      {addedDiffs.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            ➕ Added Competencies
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
            {addedDiffs.length} new competency(ies) added
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {addedDiffs.map((diff, index) => (
              <Accordion key={`${diff.competencyId}-${index}`}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">
                    ➕ {diff.competencyDescription.substring(0, 80)}
                    {diff.competencyDescription.length > 80 ? '...' : ''}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {diff.modified && (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Selected:</strong> {diff.modified.competency.selected ? '✅' : '❌'}{' '}
                        {String(diff.modified.competency.selected)}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Confidence:</strong> {diff.modified.competency.confidence_score?.toFixed(2) || 'N/A'}
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      )}

      {/* Restore Original Confirmation Dialog */}
      {showRestoreDialog && (
        <Paper 
          sx={{ 
            position: 'fixed', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)', 
            zIndex: 1300,
            p: 3,
            minWidth: 400,
            boxShadow: 24
          }}
        >
          <Typography variant="h6" gutterBottom>Confirm Restore Original</Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            ⚠️ This will restore the AI's original predictions and remove ALL your modifications (saved and unsaved).
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This action cannot be undone. Are you sure?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowRestoreDialog(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleRestoreOriginal}>
              Restore Original
            </Button>
          </Box>
        </Paper>
      )}
      {showRestoreDialog && (
        <Box 
          sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            bgcolor: 'rgba(0, 0, 0, 0.5)', 
            zIndex: 1299 
          }}
          onClick={() => setShowRestoreDialog(false)}
        />
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccessMessage(null)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setErrorMessage(null)} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
